import type { ClientContent, ClientContentTopic } from './interfaces';

// Explain failed reads without treating denied or unavailable data as an unpublished plan.
export function formatClientContentError(error: unknown): string {
    const details = error && typeof error === 'object' ? error as { code?: unknown; message?: unknown } : {};
    const code = typeof details.code === 'string' ? details.code.replace(/^firestore\//, '') : '';
    const messages: Record<string, string> = {
        'permission-denied': 'Access to this client’s app content was denied. Ask your service administrator to check content access.',
        unauthenticated: 'Please sign in again to view this client’s app content.',
        unavailable: 'The app content service is temporarily unavailable. Check your connection and try again.',
        'deadline-exceeded': 'Loading app content timed out. Check your connection and try again.',
        'resource-exhausted': 'The app content service is busy. Please try again later.',
        'not-found': 'The app content service could not be found. Ask your service administrator to check the connection.',
    };
    return messages[code] || (typeof details.message === 'string' && details.message.trim()
        ? `Could not load app content: ${details.message}`
        : 'Could not load app content. Please try again.');
}

// Reject incompatible snapshots instead of presenting partial data as a complete plan/history.
export function parseClientContent(value: unknown, clientId: string): ClientContent {
    const content = value as Partial<ClientContent> | null;
    const validTopics = (topics: unknown): topics is ClientContentTopic[] => Array.isArray(topics) &&
        topics.every((topic) => topic && typeof topic.id === 'string' && topic.id.length > 0 &&
            typeof topic.title === 'string' && typeof topic.completed === 'boolean' &&
            (topic.iconUrl === undefined || typeof topic.iconUrl === 'string') &&
            (topic.completedAt === undefined || typeof topic.completedAt === 'string'));
    if (!content || content.schemaVersion !== 1 || content.clientId !== clientId ||
        !validTopics(content.completedTopics) ||
        (content.topicResetAt !== undefined && (!content.topicResetAt ||
            typeof content.topicResetAt !== 'object' || Array.isArray(content.topicResetAt) ||
            !Object.values(content.topicResetAt).every((date) => typeof date === 'string'))) ||
        (content.timeZone !== undefined && typeof content.timeZone !== 'string') ||
        (content.dailyPlan !== null && (!content.dailyPlan ||
            typeof content.dailyPlan.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(content.dailyPlan.date) ||
            !validTopics(content.dailyPlan.topics)))) {
        throw new Error('The app content could not be read. Please update SuperPenguin and try again.');
    }
    return content as ClientContent;
}
